import { Plus, Users } from "lucide-react"
import { useMemo } from "react"
import { useTranslation } from "react-i18next"

import { createAttributeOptions } from "@/constants/speciality"
import { useFilter } from "@/lib/redux-search"

import { Badge } from "../../ui/badge"
import { Button } from "../../ui/button"
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from "../../ui/card"
import { Results } from "@/components/search/search-layout"
import { SearchDetailLink } from "@/components/search/search-return-navigation"
import { FACET_IDS } from "@/components/search/facet-ids"

export function ClassSearchResults<Result extends { id: string }>({
  ResultComponent,
}: {
  ResultComponent: React.ComponentType<{ result: Result }>
}) {
  return (
    <Results<Result>
      empty={<ClassResultEmpty />}
      ResultComponent={ResultComponent}
    />
  )
}

export function ClassResultEmpty() {
  const { t } = useTranslation(["search"])
  return (
    <div
      className="flex h-full flex-col items-center justify-center"
      data-testid="search-results-empty"
    >
      <h2 className="mb-2 text-2xl font-bold tracking-tight">
        {t("search:result.empty.label")}
      </h2>
      <p className="mb-8 max-w-md">{t("search:result.empty.description")}</p>
    </div>
  )
}

export function ClassResultCard({
  children,
  titleId,
}: {
  children: React.ReactNode
  titleId: string
}) {
  return (
    <li aria-labelledby={titleId}>
      <Card className="h-full p-0">{children}</Card>
    </li>
  )
}

export function ClassResultHeader({
  result,
  titleId,
}: {
  result: any
  titleId: string
}) {
  return (
    <CardHeader>
      <CardTitle className="text-lg font-normal" id={titleId}>
        {result.class_details.name}
      </CardTitle>
      <span className="text-brand-dark-grey">
        {" "}
        {result.profile_details?.name}
      </span>
    </CardHeader>
  )
}

export function ClassResultDetails({ result }: { result: any }) {
  const { t } = useTranslation("search")

  return (
    <div className="flex flex-row flex-wrap items-center gap-4 text-sm">
      <span className="inline-flex items-center gap-2">
        <Users className="size-4" />
        <span>
          {t("result.class_size", {
            min: result.class_details.class_size_range?.gte,
            max: result.class_details.class_size_range?.lte,
          })}
        </span>
      </span>
    </div>
  )
}

export function ClassResultSpecialities({ result }: { result: any }) {
  const { t } = useTranslation("search")
  const { t: tCurricula } = useTranslation("subjects", {
    keyPrefix: "curricula",
  })
  const { t: tSubjects } = useTranslation("subjects", { keyPrefix: "subjects" })

  const curriculum = useFilter(FACET_IDS.curriculum)
  const subject = useFilter(FACET_IDS.subject)
  const specialities = useFilter(FACET_IDS.specialities)

  const curriculumKey = result.class_details.curriculum_key
  const subjectKey = result.class_details.subject_key

  const specialityAttrs = useMemo(
    () =>
      createAttributeOptions("search:select_attributes").filter((attr) =>
        result.class_details.speciality_attributes.includes(attr.value),
      ),
    [result],
  )

  const hasBadges = curriculumKey || subjectKey || specialityAttrs.length > 0
  if (!hasBadges) return null

  return (
    <div className="flex flex-col gap-2">
      {(curriculumKey || subjectKey) && (
        <div className="flex flex-wrap items-center gap-4">
          {curriculumKey && (
            <BadgeRow label={t("result.curriculum")}>
              <ClickableBadge
                active={curriculum.values.includes(curriculumKey)}
                label={tCurricula(curriculumKey)}
                onClick={() => {
                  curriculum.toggleValue(curriculumKey)
                  curriculum.search()
                }}
              />
            </BadgeRow>
          )}
          {subjectKey && (
            <BadgeRow label={t("result.subject")}>
              <ClickableBadge
                active={subject.values.includes(subjectKey)}
                label={tSubjects(subjectKey)}
                onClick={() => {
                  subject.toggleValue(subjectKey)
                  subject.search()
                }}
              />
            </BadgeRow>
          )}
        </div>
      )}
      {specialityAttrs.length > 0 && (
        <BadgeRow label={t("result.specialities")}>
          {specialityAttrs.map((attr) => (
            <ClickableBadge
              key={attr.value}
              active={specialities.values.includes(attr.value)}
              label={attr.label}
              onClick={() => {
                specialities.toggleValue(attr.value)
                specialities.search()
              }}
            />
          ))}
        </BadgeRow>
      )}
    </div>
  )
}

function BadgeRow({
  label,
  children,
}: {
  label: string
  children: React.ReactNode
}) {
  return (
    <div className="flex items-center gap-2">
      <span className="text-sm font-medium">{label}</span>
      <div className="flex flex-wrap gap-2">{children}</div>
    </div>
  )
}

function ClickableBadge({
  active,
  label,
  onClick,
}: {
  active: boolean
  label: string
  onClick?: () => void
}) {
  if (active) {
    return (
      <Badge variant="active" className="gap-1">
        {label}
      </Badge>
    )
  }

  return (
    <Badge variant="outline" className="cursor-pointer gap-1" onClick={onClick}>
      <Plus className="size-3" />
      {label}
    </Badge>
  )
}

export function ClassResultFooter({ result }: { result: any }) {
  const { t } = useTranslation("search")

  return (
    <CardFooter className="pt-0">
      <Button variant="outline" size="sm" asChild>
        <SearchDetailLink href={`/search/classes/${result.id}`}>
          {t("result.view")}
        </SearchDetailLink>
      </Button>
    </CardFooter>
  )
}

export function ClassResultContent({
  children,
}: {
  children: React.ReactNode
}) {
  return <CardContent className="flex flex-col gap-6">{children}</CardContent>
}
